import React from "react";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Style "premium" : overlay plus doux, panneau luxe type Apple */
  variant?: "default" | "premium";
  /** Largeur : "md" (défaut), "lg" (max-w-2xl), "xl" (max-w-4xl, deux colonnes) */
  size?: "md" | "lg" | "xl";
  /** Pour size="xl" : pas de padding par défaut, le contenu gère son layout */
  noPadding?: boolean;
}

export const Modal = ({ isOpen, onClose, children, variant = "default", size = "md", noPadding = false }: ModalProps) => {
  if (!isOpen) return null;
  const isPremium = variant === "premium";
  const maxWidth =
    size === "xl" ? "max-w-4xl" : size === "lg" ? "max-w-2xl" : isPremium ? "max-w-[400px]" : "max-w-md";
  return (
    <div
      className={`fixed inset-0 flex items-center justify-center p-4 z-[999] duration-300 ${
        isPremium
          ? "bg-black/40 backdrop-blur-md animate-in fade-in"
          : "bg-black/50 backdrop-blur-sm animate-in fade-in"
      }`}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full max-h-[90vh] overflow-hidden relative flex flex-col ${maxWidth} ${
          isPremium
            ? "bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl border border-gray-200/60 dark:border-white/[0.06] rounded-3xl shadow-2xl shadow-black/10 dark:shadow-black/30"
            : "bg-white dark:bg-slate-800/95 dark:backdrop-blur-xl border border-gray-200/80 dark:border-white/[0.08] rounded-2xl shadow-xl"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-2 rounded-full z-10 transition-colors duration-200 ${
            isPremium
              ? "text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10"
              : "text-gray-400 dark:text-slate-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10"
          }`}
          aria-label="Fermer"
        >
          <X size={20} strokeWidth={2} />
        </button>
        <div
          className={
            noPadding
              ? "flex-1 min-h-0 flex flex-col overflow-y-auto text-gray-900 dark:text-white"
              : isPremium
                ? "flex-1 min-h-0 overflow-y-auto px-8 pt-10 pb-8 md:px-10 md:pt-12 md:pb-10 text-gray-900 dark:text-white"
                : "flex-1 min-h-0 overflow-y-auto p-6 md:p-8 text-gray-900 dark:text-white"
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
};
